/**
 * **Le Fil** — l'écran des commentaires ⟦SPEC-FIL §9.3-9.4, M3.23⟧.
 *
 * C'est le quatrième maillon d'une mécanique qui en avait trois : le moteur calcule
 * (`engine/fil.ts`), l'API accepte (`lire_commentaires.traitements`), le contenu alimente
 * (`content/comments.json`, 87 entrées) — et jusqu'ici **aucun écran ne déclenchait**. Une
 * mécanique n'existe que si les quatre maillons sont faits ; c'est le neuvième cas de ce
 * défaut recensé sur ce projet, et le plus visible de tous.
 *
 * ── Trois règles tenues ici, et aucune n'est réinventée ──────────────────────
 *
 * - **Le contenu décide des réponses proposées** (§8.2). Un commentaire peut n'autoriser que
 *   `ignorer`, ou interdire `supprimer` (celui d'un gros créateur). L'écran lit `reponses`
 *   dans `content/comments.json` — il ne dessine jamais un bouton que `resolve` refuserait.
 * - **Le lot est celui du moteur** (`filLot`) : 3, ou 4 avec le nœud *Réponses aux
 *   commentaires*. Recopier « 3 » ici ferait mentir l'écran le jour où le nœud est acheté.
 * - **Le coût affiché est celui qui sera débité** ⟦A-11 §1⟧ : il vient d'`actionCost` avec la
 *   charge utile réelle, recalculé à chaque changement de sélection. Le joueur voit sa facture
 *   de santé mentale **monter pendant qu'il choisit** — c'est exactement le dilemme de
 *   PLAN §4.10, et le forfait l'aplatissait.
 *
 * ── Ce que l'écran ne fait pas ────────────────────────────────────────────────
 *
 * Il ne calcule aucun effet. Pas de « +2 Hype » écrit en dur : les chiffres viennent des
 * constantes du moteur, et la seule chose que l'écran invente, ce sont les phrases françaises.
 * `probaHumour` est affichée telle quelle — `humour` est la seule réponse qui se joue à pile
 * ou face, et ⟦A-16⟧ demande de montrer le calcul, pas de le cacher derrière un suspense.
 *
 * ── ⟦C57 · lot 5d · A-174⟧ Moins de texte ─────────────────────────────────────
 *
 * Les 89 commentaires sont de la FICTION (décision de Matt, 02/10) : ils s'affichent tels quels, et
 * la réplique d'une vanne aussi. Tout le reste est court (`fil-court.ts`, mesuré par un test) : la
 * hype et son état en un mot, la promesse d'une réponse en une ligne de chiffres, la facture du
 * lot en une ligne. Les préambules et les explications de réponse sont derrière « 📰 Les dessous » :
 * un bouton sur l'écran (la hype, le Fil vide, les verdicts, lire sans répondre), un pictogramme
 * par commentaire (la promesse LONGUE de chacune de ses réponses), un bouton dans le bloc du
 * community manager (son mode d'emploi entier). Rien n'est supprimé.
 */

import { useState } from "react";

import {
  CM_COUT_HEBDO,
  CM_TRAITEMENT,
  HYPE_STAR,
  actionCost,
  candidatsCM,
  filLot,
  hype as hypeDe,
  probaHumour,
  type ReponseFil,
  type TraitementFil,
} from "@maxime/shared";

import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { capacite } from "../../game/moteur";
import { useRunStore } from "../../stores/runStore";

import {
  CM_ARRETE_COURT,
  CM_ATTEND_COURT,
  LIRE_SANS_REPONDRE_LONG,
  RENDU_COURT,
  RENDU_LONG,
  cmCeSoirCourt,
  cmEnPosteCourt,
  etatHypeCourt,
  etatHypeLong,
  filVideCourt,
  filVideLong,
  ligneFactureCourte,
  ligneStarCourte,
  promesse,
  promesseCourte,
  verdictHumourCourt,
  verdictHumourLong,
} from "./fil-court";

/** ⟦§4.3 · §9.3 · A-82⟧ Ce que le moteur a dit d'une réponse du dernier lot (`fil_reponse`). */
interface ReponseRendue {
  comment_id: string;
  reponse: ReponseFil;
  humour: boolean | null;
}

/** Les cinq réponses, dans l'ordre où elles se lisent — du plus doux au plus définitif. */
const ORDRE: readonly ReponseFil[] = ["coeur", "humour", "epingler", "ignorer", "supprimer"];

const LIBELLE: Record<ReponseFil, { icone: string; nom: string }> = {
  coeur: { icone: "❤", nom: "Cœur" },
  humour: { icone: "😄", nom: "Vanne" },
  epingler: { icone: "📌", nom: "Épingler" },
  ignorer: { icone: "🤷", nom: "Ignorer" },
  supprimer: { icone: "🗑", nom: "Supprimer" },
};

export function Fil() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const [choix, setChoix] = useState<Record<number, ReponseFil>>({});
  // ⟦§4.3 · §9.3 · A-82⟧ Le dernier lot traité : les 32 répliques `reponse_humour_reussie` /
  // `_ratee` du contenu n'étaient affichées par personne — le joueur ne voyait qu'une phrase
  // générique du moteur. Le moteur émet un `fil_reponse` par réponse (id + verdict, jamais le
  // texte) ; l'écran relit la réplique dans `comments.json`.
  const [dernierLot, setDernierLot] = useState<ReponseRendue[]>([]);

  if (!run) return null;
  const state = run.state;
  const c = contenu();
  // ⟦§9.5 · A-82⟧ Le community manager : visible dès que *Le Studio* porte la capacité.
  const cmPossede = capacite(state, "community_manager");
  const cmEnService = cmPossede && state.flags.cm_actif !== false;
  const cmCandidats = cmEnService ? candidatsCM(state, c) : [];

  const file = state.fil ?? [];
  const lot = filLot(state);
  const traitements: TraitementFil[] = ORDRE.length
    ? file
        .filter((commentaire) => choix[commentaire.uid] !== undefined)
        .map((commentaire) => ({ uid: commentaire.uid, reponse: choix[commentaire.uid]! }))
    : [];

  // ⟦A-11 §1⟧ Le coût prévisualisé EST le coût débité : même fonction, même charge utile.
  const cout = actionCost({ type: "lire_commentaires", traitements }, state);
  const pHumour = probaHumour(state, c);

  function basculer(uid: number, reponse: ReponseFil) {
    setChoix((avant) => {
      // Recliquer la même réponse la retire — c'est la seule façon de revenir en arrière sans
      // un bouton « annuler » de plus, et ça rend la sélection réversible jusqu'au dernier clic.
      if (avant[uid] === reponse) {
        const apres = { ...avant };
        delete apres[uid];
        return apres;
      }
      // Le lot est plein : on ne remplace pas silencieusement un choix déjà fait. Le joueur
      // désélectionne lui-même — sinon il perdrait une décision sans s'en apercevoir.
      if (avant[uid] === undefined && Object.keys(avant).length >= lot) return avant;
      return { ...avant, [uid]: reponse };
    });
  }

  async function envoyer() {
    const resultat = await act({ type: "lire_commentaires", traitements });
    setChoix({});
    setDernierLot(
      (resultat?.events ?? [])
        .filter((e) => e.kind === "fil_reponse")
        .map((e) => ({
          comment_id: String(e.data.comment_id),
          reponse: e.data.reponse as ReponseFil,
          humour: typeof e.data.humour === "boolean" ? e.data.humour : null,
        })),
    );
  }

  /** La phrase d'une réponse rendue : pour une vanne, la réplique écrite pour ce commentaire. */
  function rendu(r: ReponseRendue): string {
    if (r.reponse !== "humour") return RENDU_COURT[r.reponse];
    const source = c.parCommentaire.get(r.comment_id);
    // La réplique est du contenu de fiction : elle reste telle quelle, après un verdict d'une ligne.
    const replique = r.humour ? source?.reponse_humour_reussie : source?.reponse_humour_ratee;
    const verdict = verdictHumourCourt(r.humour === true);
    return replique ? `${verdict} « ${replique} »` : verdict;
  }

  return (
    <Panneau titre="💬 Le Fil" large>
      <p className="ligne-info">
        Hype : <strong>{Math.round(hypeDe(state))}</strong> / 100 · {etatHypeCourt(hypeDe(state))}{" "}
        {/* ⟦C57 · lot 5d⟧ Le préambule d'avant, les verdicts des réponses, « lire sans répondre » : ici. */}
        <LesDessous titre="Lire le Fil">
          <DessousBloc titre="La hype">
            <p className="dessous-texte">
              Hype : {Math.round(hypeDe(state))} / 100 · {etatHypeLong(hypeDe(state))}
            </p>
          </DessousBloc>
          {file.length === 0 ? (
            <DessousBloc titre="Le Fil vide">
              <p className="dessous-texte">{filVideLong(state.subscribers < 50)}</p>
            </DessousBloc>
          ) : null}
          <DessousBloc titre="Après tes réponses">
            <DessousLignes
              lignes={[
                { icone: "❤", texte: RENDU_LONG.coeur },
                { icone: "😄", texte: verdictHumourLong(true) },
                { icone: "😬", texte: verdictHumourLong(false) },
                { icone: "📌", texte: RENDU_LONG.epingler },
                { icone: "🤷", texte: RENDU_LONG.ignorer },
                { icone: "🗑", texte: RENDU_LONG.supprimer },
              ]}
            />
          </DessousBloc>
          <DessousBloc titre="Lire sans répondre">
            <p className="dessous-texte">{LIRE_SANS_REPONDRE_LONG}</p>
          </DessousBloc>
        </LesDessous>
      </p>

      {dernierLot.length > 0 ? (
        <section className="carte fil-rendu">
          <h2 className="bloc-titre">Tes dernières réponses</h2>
          <ul className="fil-rendu-liste">
            {dernierLot.map((r, i) => (
              <li key={`${r.comment_id}-${i}`}>
                <strong>{c.parCommentaire.get(r.comment_id)?.auteur ?? r.comment_id}</strong> —{" "}
                {rendu(r)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {file.length === 0 ? (
        <section className="carte">
          <p className="ligne-info">
            {/* ⟦§3.2⟧ Sous 50 abonnés, ce n'est pas un verrou : une chaîne à trois abonnés n'a
                pas de section commentaires. On le dit comme ça — pas « débloqué à 50 ». ⟦C57⟧ Une
                phrase ici ; « c'est normal, continue de publier » est dans les dessous du Fil. */}
            {filVideCourt(state.subscribers < 50)}
          </p>
        </section>
      ) : null}

      <ul className="fil-liste">
        {file.map((commentaire) => {
          const source = c.parCommentaire.get(commentaire.comment_id);
          const retenue = choix[commentaire.uid];
          const proposees = source?.reponses ?? ORDRE;
          return (
            <li
              key={commentaire.uid}
              className={`carte fil-commentaire fil-${commentaire.tonalite}${
                commentaire.epingle ? " fil-epingle" : ""
              }`}
            >
              <p className="fil-auteur">
                {commentaire.epingle ? "📌 " : ""}
                <strong>{source?.auteur ?? commentaire.comment_id}</strong>
                {commentaire.tonalite === "star" ? " ⭐" : ""}
                {commentaire.cadeau !== null ? (
                  <span className="fil-cadeau" title="Ce commentaire porte quelque chose.">
                    {commentaire.cadeau.type === "idee"
                      ? " 💡"
                      : commentaire.cadeau.type === "hook_evenement"
                        ? " 🔗"
                        : " 📖"}
                  </span>
                ) : null}
                {/* ⟦C57 · lot 5d⟧ La promesse LONGUE de chacune des réponses proposées pour CE commentaire. */}
                <LesDessous titre={source?.auteur ?? commentaire.comment_id} variante="icone" className="fil-dessous">
                  <DessousBloc titre="Tes réponses">
                    <DessousLignes
                      lignes={ORDRE.filter((r) => proposees.includes(r)).map((r) => ({
                        icone: LIBELLE[r].icone,
                        texte: (
                          <>
                            <strong>{LIBELLE[r].nom}</strong> — {promesse(r, commentaire.tonalite, pHumour)}
                          </>
                        ),
                      }))}
                    />
                  </DessousBloc>
                </LesDessous>
              </p>
              {/* Le TEXTE vient du contenu, jamais de l'état : une correction de
                  `comments.json` s'applique rétroactivement, y compris aux runs en cours. */}
              <p className="fil-texte">{source?.texte ?? "(commentaire introuvable)"}</p>

              <div className="fil-reponses">
                {ORDRE.filter((r) => proposees.includes(r)).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`bouton mini${retenue === r ? " retenu" : ""}`}
                    disabled={pending}
                    title={promesse(r, commentaire.tonalite, pHumour)}
                    aria-pressed={retenue === r}
                    onClick={() => basculer(commentaire.uid, r)}
                  >
                    {LIBELLE[r].icone} {LIBELLE[r].nom}
                  </button>
                ))}
              </div>
              {retenue !== undefined ? (
                <p className="fil-promesse">{promesseCourte(retenue, commentaire.tonalite, pHumour)}</p>
              ) : null}
            </li>
          );
        })}
      </ul>

      {file.length > 0 ? (
        <section className="carte fil-envoi">
          <p className="ligne-info">
            {/* Le coût de SM se recalcule à chaque clic. C'est LA chose que le forfait cachait :
                trois messages adorables ne coûtent rien, trois messages haineux coûtent cher. */}
            {ligneFactureCourte({
              reponses: traitements.length,
              lot,
              mental: cout.mental_delta,
              energie: cout.energy_delta,
              heures: cout.hours,
            })}
          </p>
          {/* ⟦§4.1⟧ Le bonus `star` ne s'annonce que s'il est RÉELLEMENT dans le lot : une
              star présente dans la file mais non retenue ne rapporte rien, et l'annoncer
              quand même ferait dire au pied de panneau une chose fausse. `ignorer` ne le
              déclenche pas non plus — répondre, c'est autre chose que faire défiler. */}
          {file.some(
            (x) =>
              x.tonalite === "star" && choix[x.uid] !== undefined && choix[x.uid] !== "ignorer",
          ) ? (
            <p className="ligne-info">{ligneStarCourte(HYPE_STAR)}</p>
          ) : null}
          <button
            className="bouton primaire grand"
            type="button"
            disabled={pending}
            onClick={() => void envoyer()}
          >
            {traitements.length === 0 ? "Lire sans répondre" : "Répondre"}
          </button>
        </section>
      ) : null}

      {cmPossede ? (
        <section className="carte fil-cm">
          <h2 className="bloc-titre">🧑‍💻 Community manager</h2>
          {/* ⟦§9.5 · A-82⟧ La délégation du Fil — et la porte de sortie. Les chiffres viennent
              du moteur. Ce qu'il prendrait ce soir est affiché AVANT la nuit ⟦A-16⟧ : le joueur
              qui veut répondre lui-même à l'un d'eux le fait maintenant, et le CM prendra le
              suivant. Il ne touche jamais une star, un cadeau, un toxique, un épinglé.
              ⟦C57 · lot 5d⟧ Une ligne par état ; le mode d'emploi entier est dans les dessous. */}
          {!state.flags.grand_saut_done ? (
            <p className="ligne-info">{CM_ATTEND_COURT}</p>
          ) : cmEnService ? (
            <>
              <p className="ligne-info">
                {cmEnPosteCourt({ traitement: CM_TRAITEMENT, coutHebdo: CM_COUT_HEBDO })}
              </p>
              <p className="fil-promesse">
                {cmCeSoirCourt(
                  cmCandidats.map((x) => c.parCommentaire.get(x.comment_id)?.auteur ?? x.comment_id),
                )}
              </p>
            </>
          ) : (
            <p className="ligne-info">{CM_ARRETE_COURT}</p>
          )}
          <LesDessous titre="Le community manager">
            <DessousBloc>
              {!state.flags.grand_saut_done ? (
                <p className="dessous-texte">
                  Il attend le Grand Saut : avant, il n'y a pas de caisse pour le payer.
                </p>
              ) : cmEnService ? (
                <>
                  <p className="dessous-texte">
                    En poste. La nuit, il répond cœur à {CM_TRAITEMENT} commentaires de routine —
                    moitié moins bien que toi — pour {CM_COUT_HEBDO} ₵ la semaine, au prorata. Les
                    stars, les cadeaux et les toxiques, il te les laisse.
                  </p>
                  <p className="dessous-texte">
                    {cmCandidats.length === 0
                      ? "Ce soir, rien de routine dans la file : il sera payé à ne rien faire."
                      : `Ce soir, il prendrait : ${cmCandidats
                          .map((x) => `« ${c.parCommentaire.get(x.comment_id)?.auteur ?? x.comment_id} »`)
                          .join(", ")}.`}
                  </p>
                </>
              ) : (
                <p className="dessous-texte">
                  Arrêté. Le Fil est à toi, et plus un Click ne part.
                </p>
              )}
            </DessousBloc>
          </LesDessous>
          {state.flags.grand_saut_done ? (
            <button
              className="bouton"
              type="button"
              disabled={pending}
              onClick={() => void act({ type: "regler_cm", actif: !cmEnService })}
            >
              {cmEnService ? "Reprendre la main" : "Lui confier la routine"}
            </button>
          ) : null}
        </section>
      ) : null}
    </Panneau>
  );
}
